import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useLazyGetAuthJwtQuery } from '@repo/store/maps/api/mapsApi';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectTitleSlug } from '@repo/store/maps/slices/mapData';
import { useRef, useState } from 'react';
import { getGameFontDownloadUrl, getGameFontUrl } from '../../game-map/hooks/fontUtils';
import { invalidateGameFontIcons } from './game-font-icons';
const WORKER_SERVICE = 'cloudflare-worker';
const MAX_GAME_FONT_SIZE = 5 * 1024 * 1024;
async function getResponseError(response: Response): Promise<string> {
  const body: unknown = await response.json().catch(() => null);
  if (body && typeof body === 'object') {
    const error = Reflect.get(body, 'error');
    if (typeof error === 'string') return error;
  }
  return `Request failed with status ${response.status}`;
}
export const GameFontTools = () => {
  const fontId = useAppSelector(selectTitleSlug);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fetchAuthJwt] = useLazyGetAuthJwtQuery();
  const handleUpload = async (file: File) => {
    if (!fontId) return;
    if (!file.name.toLocaleLowerCase().endsWith('.ttf')) {
      toast({
        title: 'Select a TTF font file',
        variant: 'error'
      });
      return;
    }
    if (file.size > MAX_GAME_FONT_SIZE) {
      toast({
        title: 'Font is too large',
        description: 'Game fonts must be 5 MB or smaller.',
        variant: 'error'
      });
      return;
    }
    setIsUploading(true);
    try {
      const authToken = await fetchAuthJwt(WORKER_SERVICE).unwrap();
      const response = await fetch(getGameFontUrl(fontId), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'font/ttf'
        },
        body: file
      });
      if (!response.ok) throw new Error(await getResponseError(response));
      const result: unknown = await response.json();
      const glyphCount = result && typeof result === 'object' ? Reflect.get(result, 'glyphCount') : null;
      invalidateGameFontIcons(fontId);
      toast({
        title: 'Game font uploaded',
        description: typeof glyphCount === 'number' ? `${glyphCount} icons are available after reloading the map.` : 'The new icons are available after reloading the map.',
        variant: 'success'
      });
    } catch (error) {
      toast({
        title: 'Failed to upload game font',
        description: error instanceof Error ? error.message : String(error),
        variant: 'error'
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const handleDownload = () => {
    if (!fontId) return;
    const anchor = document.createElement('a');
    anchor.href = getGameFontDownloadUrl(fontId);
    anchor.download = `${fontId}.ttf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };
  return <FlexColumn gap="8" width="full">
            <Type variant="body-s" color="text.secondary">
                Upload or download the icon font for {fontId ?? 'the current game'}. Uploading replaces the active font
                for every map in this game.
            </Type>
            <FlexRow gap="8" width="full">
                <Button onClick={() => fileInputRef.current?.click()} color="secondary" size="sm" fullWidth disabled={isUploading || !fontId}>
                    <Icon icon="upload" decorative />
                    {isUploading ? 'Uploading…' : 'Upload TTF'}
                </Button>
                <Button onClick={handleDownload} color="secondary" size="sm" fullWidth disabled={!fontId || isUploading}>
                    <Icon icon="download" decorative />
                    Download TTF
                </Button>
            </FlexRow>
            <input ref={fileInputRef} type="file" accept=".ttf,font/ttf,application/x-font-ttf" hidden onChange={event => {
      const file_0 = event.currentTarget.files?.[0];
      if (file_0) void handleUpload(file_0);
    }} />
        </FlexColumn>;
};